import React, { useMemo } from 'react';
import { State } from 'country-state-city';
import { AutocompleteSelect, type AutocompleteOption } from './AutocompleteSelect';
import * as countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countriesLib.registerLocale(enLocale);

/**
 * Look up ISO 3166-1 alpha-2 country code from a full country name.
 */
function getCountryCode(name: string): string | null {
  const names = countriesLib.getNames('en');
  const entry = Object.entries(names).find(
    ([, n]) => n.toLowerCase() === name.toLowerCase()
  );
  return entry ? entry[0] : null;
}

interface StateSelectProps {
  value?: string;
  onChange: (value: string) => void;
  countryName?: string;
  disabled?: boolean;
}

const StateSelect: React.FC<StateSelectProps> = ({
  value,
  onChange,
  countryName,
  disabled,
}) => {
  const stateOptions: AutocompleteOption[] = useMemo(() => {
    if (!countryName) return [];

    const countryCode = getCountryCode(countryName);
    if (!countryCode) return [];

    const states = State.getStatesOfCountry(countryCode);
    if (!states || states.length === 0) return [];

    return states
      .map((s) => ({ value: s.name, label: s.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countryName]);

  return (
    <AutocompleteSelect
      value={value}
      onChange={onChange}
      options={stateOptions}
      placeholder={countryName ? 'Select state...' : 'Select country first...'}
      searchPlaceholder="Search states..."
      emptyText="No state found."
      creatable
      disabled={disabled || !countryName}
    />
  );
};

export { StateSelect };
